/**
 * Frontend helpers for ops unsafe-area settings (tiers, duplicates).
 */

export const RISK_TIER_GUIDE = [
  {
    tier: 1,
    severity: "critical",
    label: "Tier 1: Critical Risk",
    summary:
      "Highest operational threat — transit interference, interception, or intersection security risks.",
    opsAction:
      "Follow the area’s safety protocol strictly (often daylight-only / constant dispatch). Booking confirmation requires acknowledgment.",
  },
  {
    tier: 2,
    severity: "high",
    label: "Tier 2: Elevated Risk",
    summary:
      "Elevated theft or volatility during congestion, offloading, or localized unrest.",
    opsAction:
      "Secure cargo, minimize stationary time, check local alerts. Booking confirmation requires acknowledgment.",
  },
  {
    tier: 3,
    severity: "medium",
    label: "Tier 3: Property Risk",
    summary:
      "Property / corridor risk — interceptions on logistics corridors or residential access issues.",
    opsAction: "Vary routes; verify client identity and location before arrival.",
  },
  {
    tier: 4,
    severity: "low",
    label: "Tier 4: General Risk",
    summary: "General opportunistic transit risk in busy commercial corridors.",
    opsAction: "Use reinforced seals; stay alert in transit.",
  },
];

export const TERMINOLOGY = [
  {
    term: "Region / Place",
    meaning:
      "Suburb, township, or CBD name matched against pickup and delivery addresses (and aliases).",
  },
  {
    term: "Province",
    meaning:
      "Used to reduce false matches when the same place name exists in more than one province.",
  },
  {
    term: "Primary logistics concern",
    meaning: "The main operational risk for crews on this route or stop.",
  },
  {
    term: "General safety protocol",
    meaning:
      "Standing instruction ops and crew should follow when this area is involved.",
  },
  {
    term: "Severity",
    meaning:
      "Mapped from tier for UI urgency: critical → Tier 1, high → Tier 2, medium → Tier 3, low → Tier 4.",
  },
];

const SEVERITY_FROM_TIER = {
  1: "critical",
  2: "high",
  3: "medium",
  4: "low",
};

const RISK_CATEGORY_FROM_TIER = {
  1: "Tier 1: Critical Risk",
  2: "Tier 2: Elevated Risk",
  3: "Tier 3: Property Risk",
  4: "Tier 4: General Risk",
};

/**
 * @param {string} value
 * @returns {string}
 */
export function normalizeAreaKey(value) {
  if (!value || typeof value !== "string") return "";
  return value
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * @param {Object|string} entry
 * @returns {string[]}
 */
export function collectAreaIdentityKeys(entry) {
  if (typeof entry === "string") {
    const key = normalizeAreaKey(entry);
    return key ? [key] : [];
  }
  if (!entry || typeof entry !== "object") return [];

  const keys = [];
  const name = normalizeAreaKey(entry.name || "");
  if (name) keys.push(name);
  if (Array.isArray(entry.aliases)) {
    for (const alias of entry.aliases) {
      const normalized = normalizeAreaKey(alias);
      if (normalized) keys.push(normalized);
    }
  }
  return [...new Set(keys)];
}

/**
 * @param {Array} areas
 * @param {Object|string|null} [candidate]
 * @returns {{ hasDuplicates: boolean, conflicts: Array<{ key: string, names: string[] }> }}
 */
export function findUnsafeAreaDuplicates(areas = [], candidate = null) {
  const list = Array.isArray(areas) ? areas : [];
  const keyOwners = new Map();

  const register = (entry) => {
    const display =
      typeof entry === "string" ? entry : entry?.name || "unknown";
    for (const key of collectAreaIdentityKeys(entry)) {
      if (!keyOwners.has(key)) keyOwners.set(key, []);
      keyOwners.get(key).push(display);
    }
  };

  if (candidate != null) {
    list.forEach(register);
    const conflicts = [];
    for (const key of collectAreaIdentityKeys(candidate)) {
      const owners = keyOwners.get(key) || [];
      if (owners.length > 0) {
        conflicts.push({
          key,
          names: [
            ...new Set([
              ...owners,
              typeof candidate === "string" ? candidate : candidate.name,
            ]),
          ],
        });
      }
    }
    return { hasDuplicates: conflicts.length > 0, conflicts };
  }

  list.forEach(register);
  const conflicts = [];
  for (const [key, names] of keyOwners.entries()) {
    if (names.length > 1) {
      conflicts.push({ key, names: [...new Set(names)] });
    }
  }
  return { hasDuplicates: conflicts.length > 0, conflicts };
}

/**
 * @param {number} tier
 * @returns {Object}
 */
export function buildAreaFromForm({
  name,
  aliasesText = "",
  province,
  regionGroup = "",
  tier,
  primaryLogisticsConcern = "",
  generalSafetyProtocol = "",
}) {
  const tierNum = Number(tier) || 3;
  const aliases = String(aliasesText || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  return {
    name: String(name || "").trim(),
    aliases,
    province: String(province || "").trim(),
    regionGroup: String(regionGroup || name || "").trim(),
    tier: tierNum,
    riskCategory: RISK_CATEGORY_FROM_TIER[tierNum] || `Tier ${tierNum}`,
    severity: SEVERITY_FROM_TIER[tierNum] || "medium",
    primaryLogisticsConcern: String(primaryLogisticsConcern || "").trim(),
    generalSafetyProtocol: String(generalSafetyProtocol || "").trim(),
    notes: String(generalSafetyProtocol || "").trim(),
  };
}

/**
 * @param {string} severity
 * @returns {string}
 */
export function tierTagColor(severity) {
  if (severity === "critical") return "red";
  if (severity === "high") return "orange";
  if (severity === "medium") return "gold";
  return "blue";
}
